import React, { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition, Listbox, RadioGroup } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { convertCurrency } from '../../utils/calculations';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const AllocationModal = ({ visible, onCancel, portfolioId, goalId, allocation }) => {
  const { goals, portfolios, updateGoal, exchangeRates } = useData();
  const portfolio = portfolios.find(p => p.id === portfolioId);
  const goal = goals.find(g => g.id === goalId);

  const [formData, setFormData] = useState({
    goalId: goalId || '',
    portfolioId: portfolioId || '',
    type: 'percentage',
    value: 0,
  });
  const [errors, setErrors] = useState({});

  // Close modal on Escape key
  useEscapeKey(onCancel, visible);

  useEffect(() => {
    if (allocation) {
      setFormData({
        goalId: allocation.goalId || goalId || '',
        portfolioId: allocation.portfolioId || portfolioId || '',
        type: allocation.type || 'percentage',
        value: allocation.value || 0,
      });
    } else if (visible) {
      setFormData({
        goalId: goalId || '',
        portfolioId: portfolioId || '',
        type: 'percentage',
        value: 0,
      });
    }
    setErrors({});
  }, [allocation, visible, goalId, portfolioId]);

  const validate = () => {
    const newErrors = {};
    if (!formData.goalId) {
      newErrors.goalId = 'Please select a goal';
    }
    if (!formData.portfolioId) {
      newErrors.portfolioId = 'Please select an account';
    }
    if (formData.value === null || formData.value === undefined || formData.value < 0) {
      newErrors.value = 'Please enter a value';
    }
    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const selectedGoal = goals.find(g => g.id === formData.goalId);
    if (!selectedGoal) return;

    const currentAllocations = selectedGoal.allocations || [];

    const updatedAllocations = currentAllocations.filter(a => a.portfolioId !== formData.portfolioId);

    if (formData.value > 0) {
      updatedAllocations.push({
        portfolioId: formData.portfolioId,
        type: formData.type,
        value: formData.value,
      });
    }

    updateGoal(selectedGoal.id, { allocations: updatedAllocations });

    setFormData({
      goalId: goalId || '',
      portfolioId: portfolioId || '',
      type: 'percentage',
      value: 0,
    });
    onCancel();
  };

  const handleCancel = () => {
    setFormData({
      goalId: goalId || '',
      portfolioId: portfolioId || '',
      type: 'percentage',
      value: 0,
    });
    setErrors({});
    onCancel();
  };

  const selectedGoal = goals.find(g => g.id === formData.goalId);
  const selectedPortfolio = portfolios.find(p => p.id === formData.portfolioId);

  const calculateConvertedValue = () => {
    if (!selectedPortfolio || !selectedGoal || !formData.value) return 0;

    let allocatedValue = 0;
    if (formData.type === 'percentage') {
      allocatedValue = selectedPortfolio.currentValue * (formData.value / 100);
    } else {
      allocatedValue = formData.value;
    }

    try {
      return convertCurrency(
        allocatedValue,
        selectedPortfolio.currency,
        selectedGoal.currency,
        exchangeRates
      );
    } catch (error) {
      console.error('Currency conversion error:', error);
      return allocatedValue; // Return unconverted value as fallback
    }
  };

  const goalOptions = goals.map(g => ({ id: g.id, name: `${g.emoji} ${g.name}` }));
  const selectedGoalOption = goalOptions.find(g => g.id === formData.goalId);

  const portfolioOptions = portfolios.map(p => ({ id: p.id, name: p.name }));
  const selectedPortfolioOption = portfolioOptions.find(p => p.id === formData.portfolioId);

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-2">
                  {allocation ? 'Edit Allocation' : 'Add Allocation'}
                </Dialog.Title>

                <hr className="mb-3 border-gray-200" />

                <form onSubmit={handleSubmit} className="space-y-2">
                  {/* Portfolio Selection - Show only if coming from goal page */}
                  {goalId && !portfolioId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account <span className="text-red-600">*</span>
                    </label>
                    <Listbox
                      value={selectedPortfolioOption}
                      onChange={(value) => setFormData({ ...formData, portfolioId: value.id })}
                      disabled={!!allocation}
                    >
                      <div className="relative">
                        <Listbox.Button className={`relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.portfolioId ? 'border-red-500' : 'border-gray-300'
                        } ${allocation ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <span className="block truncate">
                            {selectedPortfolioOption ? selectedPortfolioOption.name : 'Select an account'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        {!allocation && (
                          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {portfolioOptions.map((portfolio) => (
                                <Listbox.Option
                                  key={portfolio.id}
                                  value={portfolio}
                                  className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
                                >
                                  {({ selected }) => (
                                    <>
                                      <span className={selected ? 'font-medium' : 'font-normal'}>{portfolio.name}</span>
                                      {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        )}
                      </div>
                    </Listbox>
                    {errors.portfolioId && <p className="mt-1 text-sm text-red-600">{errors.portfolioId}</p>}
                  </div>
                  )}

                  {/* Goal Selection - Show only if coming from portfolio page */}
                  {portfolioId && !goalId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Goal <span className="text-red-600">*</span>
                    </label>
                    <Listbox
                      value={selectedGoalOption}
                      onChange={(value) => setFormData({ ...formData, goalId: value.id })}
                      disabled={!!allocation}
                    >
                      <div className="relative">
                        <Listbox.Button className={`relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.goalId ? 'border-red-500' : 'border-gray-300'
                        } ${allocation ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <span className="block truncate">
                            {selectedGoalOption ? selectedGoalOption.name : 'Select a goal'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        {!allocation && (
                          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {goalOptions.map((goal) => (
                                <Listbox.Option
                                  key={goal.id}
                                  value={goal}
                                  className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
                                >
                                  {({ selected }) => (
                                    <>
                                      <span className={selected ? 'font-medium' : 'font-normal'}>{goal.name}</span>
                                      {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        )}
                      </div>
                    </Listbox>
                    {errors.goalId && <p className="mt-1 text-sm text-red-600">{errors.goalId}</p>}
                  </div>
                  )}

                  {/* Allocation Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allocation Type <span className="text-red-600">*</span>
                    </label>
                    <RadioGroup value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })}>
                      <div className="space-y-2">
                        <RadioGroup.Option value="percentage">
                          {({ checked }) => (
                            <div className={`flex items-center cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                              checked ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                            }`}>
                              <div className="flex items-center">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  checked ? 'border-blue-500' : 'border-gray-300'
                                }`}>
                                  {checked && <div className="w-3 h-3 rounded-full bg-blue-500" />}
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-900">Percentage</span>
                              </div>
                            </div>
                          )}
                        </RadioGroup.Option>
                        <RadioGroup.Option value="amount">
                          {({ checked }) => (
                            <div className={`flex items-center cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                              checked ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                            }`}>
                              <div className="flex items-center">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  checked ? 'border-blue-500' : 'border-gray-300'
                                }`}>
                                  {checked && <div className="w-3 h-3 rounded-full bg-blue-500" />}
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-900">
                                  Amount ({getCurrencySymbol(selectedPortfolio?.currency || 'GBP')})
                                </span>
                              </div>
                            </div>
                          )}
                        </RadioGroup.Option>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Value Input */}
                  <div>
                    <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.type === 'percentage' ? 'Percentage (%)' : `Amount (${getCurrencySymbol(portfolio.currency)})`} <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="value"
                      type="number"
                      step="0.01"
                      min="0"
                      max={formData.type === 'percentage' ? 100 : undefined}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                      className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.value ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.value && <p className="mt-1 text-sm text-red-600">{errors.value}</p>}
                  </div>

                  {/* Allocation Preview */}
                  {selectedGoal && formData.value > 0 && (
                    <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                      <p className="text-xs text-gray-600">
                        <strong>Portfolio:</strong> {formatCurrency(portfolio.currentValue, portfolio.currency, 0)}
                      </p>
                      <p className="text-xs text-gray-600">
                        <strong>Allocated:</strong>{' '}
                        {formData.type === 'percentage'
                          ? `${formData.value}% = ${formatCurrency(portfolio.currentValue * (formData.value / 100), portfolio.currency, 0)}`
                          : formatCurrency(formData.value, portfolio.currency, 0)}
                      </p>
                      {selectedGoal.currency !== portfolio.currency && (
                        <p className="text-xs text-gray-600">
                          <strong>In goal currency:</strong> ≈ {formatCurrency(calculateConvertedValue(), selectedGoal.currency, 0)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white  rounded-lg  transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                    >
                      {allocation ? 'Update' : 'Add'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AllocationModal;
